/**
 * Quantity promo — cart progress nudge driver.
 *
 * Powers <qb-promo-bar>. Primary source of truth is Venue's cart store
 * (window.theme.cart.store) — the same store cart-free-shipping-bar subscribes to.
 * Adds/changes/removes flow through that store, so one subscription keeps the bar
 * in sync across the drawer and cart page.
 *
 * Fallback: if the store is never available, we patch fetch to watch cart-write
 * requests and re-read /cart.js after them. Everything is wrapped so a failure here
 * can never break the cart — on any error we leave the server-rendered state in place.
 */
(function () {
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var EVENT = 'qb:cart-updated';

  function root() {
    try {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    } catch (e) {
      return '/';
    }
  }

  function getStore() {
    try {
      var s = window.theme && window.theme.cart && window.theme.cart.store;
      return s && typeof s.subscribe === 'function' ? s : null;
    } catch (e) {
      return null;
    }
  }

  function valuesFromState(state) {
    var out = { count: null, total: null };
    try {
      if (state && state.products) {
        if (typeof state.products.item_count === 'number') out.count = state.products.item_count;
        if (typeof state.products.total_price === 'number') out.total = state.products.total_price;
      }
    } catch (e) {}
    return out;
  }

  function formatMoney(cents) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var fmt = (window.theme && window.theme.money_format) || '';
        return window.Shopify.formatMoney(cents, fmt).replace(/[\.,]00$/, '');
      }
    } catch (e) {}
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  function broadcast(values) {
    if (!values || typeof values.count !== 'number') return;
    try {
      document.dispatchEvent(new CustomEvent(EVENT, { detail: values }));
    } catch (e) {}
  }

  /* ---- fetch fallback (only matters when the store is absent) ---- */
  (function patchFetch() {
    try {
      if (window.__qbPromoFetchPatched || typeof window.fetch !== 'function') return;
      window.__qbPromoFetchPatched = true;
      var orig = window.fetch;
      window.fetch = function () {
        var promise = orig.apply(this, arguments);
        try {
          var req = arguments[0];
          var url = req && req.url ? req.url : String(req || '');
          if (CART_WRITE.test(url)) {
            promise
              .then(function () {
                // If the store exists it already broadcasts via subscribe; don't double-fetch.
                if (getStore()) return;
                orig(root() + 'cart.js')
                  .then(function (r) { return r.json(); })
                  .then(function (cart) {
                    if (cart) broadcast({ count: cart.item_count, total: cart.total_price });
                  })
                  .catch(function () {});
              })
              .catch(function () {});
          }
        } catch (e) {}
        return promise;
      };
    } catch (e) {}
  })();

  /* ---- custom element ---- */
  class QbPromoBar extends HTMLElement {
    connectedCallback() {
      try {
        this.threshold = parseInt(this.getAttribute('threshold'), 10) || 2;
        this.shippingGoal = parseInt(this.getAttribute('shipping-goal'), 10) || 0; // cents; 0 = disabled
        this.singular = this.getAttribute('data-singular') || '';
        this.plural = this.getAttribute('data-plural') || '';
        this.fill = this.querySelector('.qb-promo-bar__fill');
        this.awayEl = this.querySelector('[data-qb-state="away"]');
        this.shippingEl = this.querySelector('[data-qb-state="shipping"]');
        this.unlockedEl = this.querySelector('[data-qb-state="unlocked"]');
        this.remainingEl = this.querySelector('[data-qb-remaining]');
        this.nounEl = this.querySelector('[data-qb-noun]');
        this.moneyEl = this.querySelector('[data-qb-money]');

        this._onEvent = (e) => {
          if (e && e.detail && typeof e.detail.count === 'number') this.render(e.detail);
        };
        document.addEventListener(EVENT, this._onEvent);

        this._init(0);
      } catch (e) {}
    }

    disconnectedCallback() {
      try {
        if (this._unsubscribe) this._unsubscribe();
        this._unsubscribe = null;
        if (this._onEvent) document.removeEventListener(EVENT, this._onEvent);
      } catch (e) {}
    }

    // Wait for the cart store, then sync once and subscribe. Falls back to a single
    // /cart.js read if the store never appears.
    _init(attempt) {
      if (!this.isConnected) return; // bailed out (e.g. drawer re-rendered) before the store was ready
      const store = getStore();
      if (store) {
        try {
          this.render(valuesFromState(store.getState()));
          this._unsubscribe = store.subscribe((state) => this.render(valuesFromState(state)));
        } catch (e) {}
        return;
      }
      if (attempt < 20) {
        setTimeout(() => this._init(attempt + 1), 150);
      } else {
        this._fallbackFetch();
      }
    }

    _fallbackFetch() {
      try {
        window
          .fetch(root() + 'cart.js')
          .then((r) => r.json())
          .then((cart) => {
            if (cart) this.render({ count: cart.item_count, total: cart.total_price });
          })
          .catch(() => {});
      } catch (e) {}
    }

    render(values) {
      try {
        const count = values && values.count;
        if (typeof count !== 'number' || isNaN(count)) return; // leave server state untouched
        const total = values && typeof values.total === 'number' ? values.total : null;

        const threshold = this.threshold;
        const qtyMet = count >= threshold;
        const shippingActive = this.shippingGoal > 0 && total !== null;
        const shipMet = shippingActive && total >= this.shippingGoal;

        // Resolve which of the three states is shown.
        let mode;
        if (!qtyMet) mode = 'away';
        else if (shippingActive && !shipMet) mode = 'shipping';
        else mode = 'unlocked';

        // Progress fill tracks the goal currently in view.
        let pct;
        if (mode === 'away') pct = (count / threshold) * 100;
        else if (mode === 'shipping') pct = (total / this.shippingGoal) * 100;
        else pct = 100;
        pct = Math.max(0, Math.min(100, Math.round(pct)));

        if (this.fill) this.fill.style.width = pct + '%';
        if (qtyMet) this.setAttribute('data-qb-unlocked', '');
        else this.removeAttribute('data-qb-unlocked');

        if (this.awayEl) this.awayEl.hidden = mode !== 'away';
        if (this.shippingEl) this.shippingEl.hidden = mode !== 'shipping';
        if (this.unlockedEl) this.unlockedEl.hidden = mode !== 'unlocked';

        if (mode === 'away') {
          const remaining = Math.max(threshold - count, 0);
          if (this.remainingEl) this.remainingEl.textContent = remaining;
          if (this.nounEl) this.nounEl.textContent = remaining === 1 ? this.singular : this.plural;
        } else if (mode === 'shipping' && this.moneyEl) {
          this.moneyEl.textContent = formatMoney(Math.max(this.shippingGoal - total, 0));
        }
      } catch (e) {}
    }
  }

  try {
    if (window.customElements && !customElements.get('qb-promo-bar')) {
      customElements.define('qb-promo-bar', QbPromoBar);
    }
  } catch (e) {}
})();
